import React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Slide } from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import type { ErrorDialogValue } from './index';
import Typography from '@mui/material/Typography';
import type { AxiosError } from 'axios';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface ErrorDialogComponentProps {
  errorDialogValue: ErrorDialogValue;

  closeErrorDialog(): void;
}

export const ErrorDialogComponent: React.FC<ErrorDialogComponentProps> = ({ errorDialogValue, closeErrorDialog }) => {
  const errorComponent = errorDialogValue.error.isAxiosError ? (
    <AxiosErrorDisplay error={errorDialogValue.error as AxiosError} />
  ) : (
    <GenericErrorDisplay error={errorDialogValue.error} />
  );

  return (
    <Dialog open TransitionComponent={Transition} keepMounted onClose={closeErrorDialog} sx={{
      zIndex: (theme) => theme.zIndex.modal + 2,
    }}>
      <DialogTitle>An error occurred</DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Typography variant="body1">
            Operation{' '}
            <strong>
              {'"'}
              {errorDialogValue.operationDescription}
              {'"'}
            </strong>{' '}
            failed with error:
          </Typography>
          <Typography variant="body1">{errorDialogValue.error.message}</Typography>

          {errorComponent}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeErrorDialog}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

interface AxiosErrorDisplayProps {
  error: AxiosError;
}

function isDomElement(value: unknown): value is Element {
  return typeof Element !== 'undefined' && value instanceof Element;
}

function safeJsonStringify(value: unknown): string {
  const seen = new WeakSet<object>();
  try {
    return JSON.stringify(
      value,
      (_key, currentValue) => {
        if (isDomElement(currentValue)) {
          const id = currentValue.id ? `#${currentValue.id}` : '';
          const className =
            typeof currentValue.className === 'string' && currentValue.className.length > 0
              ? `.${currentValue.className.trim().replace(/\s+/g, '.')}`
              : '';
          return `[DOMElement ${currentValue.tagName.toLowerCase()}${id}${className}]`;
        }

        if (typeof currentValue === 'object' && currentValue !== null) {
          if (seen.has(currentValue)) {
            return '[Circular]';
          }
          seen.add(currentValue);
        }

        return currentValue;
      },
      2
    );
  } catch {
    return '[Unserializable response data]';
  }
}

function parseErrorMessage(responseData: string): string | undefined {
  try {
    const errorMessage = JSON.parse(responseData).error?.message;
    if (typeof errorMessage === 'string') {
      return errorMessage;
    }
  } catch (error) {
    // ignore
  }
  return undefined;
}

export const AxiosErrorDisplay: React.FC<AxiosErrorDisplayProps> = ({ error }) => {
  const method = (error.config?.method || '').toUpperCase();
  const url = error.request?.responseURL;
  const responseData =
    typeof error.response?.data === 'string'
      ? error.response?.data
      : safeJsonStringify(error.response?.data);

  const errorMessage = parseErrorMessage(responseData);
  return (
    <>
      {Boolean(errorMessage) && (
        <div>
          <Typography variant="h5">Error message</Typography>
          <Typography variant="body1">{errorMessage}</Typography>
        </div>
      )}
      <div>
        <Typography variant="h5">HTTP request</Typography>
        <Typography variant="body1">
          <b>{method}</b> {url}
        </Typography>
      </div>
      <div>
        <Typography variant="h5">HTTP response</Typography>
        <Typography variant="body1">
          <b>Status:</b> {error.response?.status}
        </Typography>
        <Typography variant="body1">
          <b>Data:</b>
        </Typography>
        <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
          {responseData}
        </Typography>
      </div>
    </>
  );
};

interface GenericErrorDisplayProps {
  error: any;
}

export const GenericErrorDisplay: React.FC<GenericErrorDisplayProps> = ({ error }) => {
  return (
    <>
      {error.description && (
        <p>
          <Typography variant="h5">Description</Typography>
          <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
            {error.description}
          </Typography>
        </p>
      )}
      <p>
        <Typography variant="h5">Content</Typography>
        {error.name && <Typography variant="body1">{error.name}</Typography>}
        <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
          {error.message}
        </Typography>
      </p>
    </>
  );
};
